import { HttpClient, HttpEventType, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AppConsts } from '@shared/AppConsts';

export interface CreateElectricalStandardDto {
    title: string;
    description: string;
    path: string;
    fileName: string;
    statusTag: string;
}

export interface UpdateElectricalStandardDto {
    id: number;
    title: string;
    description: string;
    path: string;
    fileName: string;
    statusTag: string;
}

export interface ElectricalStandardDto {
    id: number;
    title: string;
    description: string;
    path: string;
    fileName: string;
    statusTag?: string;
    creationTime?: string;
}

export interface GetElectricalStandardsInput {
    offset: number;
    pageSize: number;
    searchParms: string;
}

export interface CommonResponseDto<T> {
    result: T;
}

export interface UploadFileResponseDto {
    path: string;
    fileName: string;
}

export interface SignUrlResponseDto {
    url: string;
    expiresAt: string;
}


export interface PagedResultDto<T> {
    totalCount: number;
    items: T[];
}

@Injectable({ providedIn: 'root' })
export class ElectricalStandardsService {
    private readonly baseUrl = `${AppConsts.remoteServiceBaseUrl}/api/electricalstandards`;

    constructor(private http: HttpClient) {
    }

    async create(input: CreateElectricalStandardDto): Promise<number> {
        const response: any = await this.http.post<any>(`${this.baseUrl}/create`, input).toPromise();
        return response?.id ?? response;
    }

    async get(input: GetElectricalStandardsInput): Promise<PagedResultDto<ElectricalStandardDto>> {
        let params = new HttpParams();
        params = params.set('offset', input.offset.toString());
        params = params.set('pageSize', input.pageSize.toString());
        params = params.set('searchParms', input.searchParms ?? '');

        const response: any = await this.http.get<any>(`${this.baseUrl}/get`, { params }).toPromise();
        return this.unwrapResponse<PagedResultDto<ElectricalStandardDto>>(response);
    }

    async update(input: UpdateElectricalStandardDto): Promise<void> {
        await this.http.post<void>(`${this.baseUrl}/update`, input).toPromise();
    }

    async delete(id: number): Promise<void> {
        const params = new HttpParams().set('id', id.toString());

        await this.http.delete<void>(`${this.baseUrl}/delete`, { params }).toPromise();
    }

    async uploadFile(file: File, progressCallback?: (percent: number) => void): Promise<UploadFileResponseDto> {
        const formData = new FormData();
        formData.append('file', file);

        return new Promise<UploadFileResponseDto>((resolve, reject) => {
            this.http.post<any>(`${this.baseUrl}/uploadfile`, formData, {
                reportProgress: true,
                observe: 'events',

            })
            .subscribe(event => {
                    console.log(event.type, event);

                    if (event.type === HttpEventType.UploadProgress && progressCallback) {
                        const progress = Math.round(100 * event.loaded / (event.total ?? event.loaded));
                        console.log(progress);
                        progressCallback(progress);
                    }

                    if (event.type === HttpEventType.Response) {
                        console.log('Completed');
                        resolve(this.unwrapResponse<UploadFileResponseDto>(event.body));
                    }
                });



        });
    }

    async createSignUrl(id: number): Promise<SignUrlResponseDto> {
        const params = new HttpParams().set('id', id.toString());

        const response: any = await this.http.get<any>(`${this.baseUrl}/createsignurl`, { params }).toPromise();
        return this.unwrapResponse<SignUrlResponseDto>(response);
    }

    private unwrapResponse<T>(response: any): T {
        if (response && typeof response === 'object' && 'result' in response) {
            return response.result as T;
        }

        return response as T;
    }
}
